import { Link } from 'react-router-dom';
import { PageHeader } from '../components/layout/PageHeader';

export function TermsPage() {
  return (
    <>
      <PageHeader
        title="Terms & Conditions"
        subtitle="Last updated: 23 May 2026"
      />

      <article className="page-pad pb-8">
        <div className="mx-auto max-w-3xl">
          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">Overview</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              These Terms and Conditions govern your access to and use of the DIH
              Document Organisation system, including the web console and all
              associated services provided by DIH. By accessing or using the system,
              you acknowledge that you have read, understood, and agree to be bound
              by these terms.
            </p>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">1. Definitions</p>
            <dl className="space-y-2 text-[0.8125rem] leading-relaxed text-body">
              <div>
                <dt className="font-semibold text-petrol">"System"</dt>
                <dd className="ml-4 text-muted-slate">
                  The DIH Hub platform, including all functions
                  for document triage, routing, merging, lifecycle management, and
                  register maintenance.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-petrol">"User"</dt>
                <dd className="ml-4 text-muted-slate">
                  Any individual who has been granted access to the System through a
                  DIH Azure Active Directory identity.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-petrol">"Content"</dt>
                <dd className="ml-4 text-muted-slate">
                  All documents, files, metadata, and data processed by or stored
                  within the System.
                </dd>
              </div>
            </dl>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">2. Access & Authentication</p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                Access to the System is restricted to authorised personnel with
                valid DIH Azure Active Directory credentials.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                You are responsible for maintaining the confidentiality of your
                credentials and for all activities conducted under your account.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                Administrative functions are available only to users with the Admin
                role. Unauthorised attempts to access admin features are logged and
                may result in access revocation.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                DIH reserves the right to suspend or terminate access at any time
                without prior notice if a breach of these terms is suspected.
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">3. Acceptable Use</p>
            <p className="mb-2 text-[0.8125rem] leading-relaxed text-body">
              You agree to use the System solely for its intended business purposes.
              You shall not:
            </p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                Attempt to circumvent any security controls, authentication
                mechanisms, or access restrictions.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                Use the System to store, transmit, or process data unrelated to DIH
                operations.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                Reverse-engineer, decompile, or disassemble any part of the System.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                Share, redistribute, or make the System available to third parties
                without prior written consent from DIH.
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">4. Intellectual Property</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              All intellectual property rights in the System, including its
              architecture, code, design, documentation, and brand assets, are owned
              by DIH. Nothing in these Terms grants you any ownership interest in
              the System. Content processed through the System remains the property
              of its respective owners.
            </p>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">5. Data Handling & Audit Trail</p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                Every mutation within the System is recorded in a tamper-evident
                audit log, including the identity of the acting user and a timestamp.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                The System processes documents using a combination of rule-based
                precedence logic and large language model (LLM) classification. LLM
                outputs are always subject to confidence gating and may be escalated
                for human review.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                For details on how your personal data is collected, used, and
                protected, please refer to our{' '}
                <Link to="/privacy" className="brand-link-btn">
                  Privacy Policy
                </Link>
                .
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">6. Limitation of Liability</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              The System is provided on an "as is" basis. DIH makes no warranties,
              express or implied, regarding the System's availability, accuracy, or
              fitness for a particular purpose. To the fullest extent permitted by
              applicable law, DIH shall not be liable for any indirect, incidental,
              or consequential damages arising from your use of the System.
            </p>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">7. Modifications</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              DIH reserves the right to modify these Terms at any time. Material
              changes will be communicated through the System. Continued use of the
              System after such notification constitutes acceptance of the updated
              terms.
            </p>
          </section>

          <section className="brand-card">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">8. Governing Law</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              These Terms shall be governed by and construed in accordance with the
              laws of the jurisdiction in which DIH operates, without regard to
              conflict of law principles.
            </p>
          </section>

          <div className="brand-note mt-6">
            If you have questions about these Terms, please contact your IT
            administrator or the DIH operations team.
          </div>
        </div>
      </article>
    </>
  );
}
